import { GlobalReferenceTypeId, GraphModelShape } from '@ardoq/data-model';
import {
  ArdoqId,
  ViewIds,
  APIComponentAttributes,
  APIFieldAttributes,
  LinkedComponent,
} from '@ardoq/api-types';
import {
  PermissionContext,
  permissionsOperations,
} from '@ardoq/access-control';
import { contextInterface } from '../../modelInterface/contextInterface';
import { workspaceInterface } from '@ardoq/workspace-interface';
import { fields$ } from 'streams/fields/fields$';
import {
  componentPropsFromData,
  emptyState,
  extractCustomFieldsFromComponentData,
  fieldHeaderProps,
  setupHeaders,
  type TableCustomFieldContext,
} from './common/utils';
import { orgUsers$ } from 'streams/orgUsers/orgUsers$';
import { documentArchiveAttachments$ } from 'streams/documentArchive/documentArchiveAttachments$';
import {
  resetExpandedDescriptionsReducer,
  toggleDescriptionReducer,
} from './common/commonReducers';
import { notifyFiltersChanged } from 'streams/filters/FilterActions';

import {
  getContextComponentChildren,
  relevantComponentsFromContextShape,
} from '../componentReducers';
import { isUnique } from 'utils/collectionUtil';
import { ContextSort, NumericSortOrder, isTruthy } from '@ardoq/common-helpers';
import {
  isReferenceIdIncludedInContextByFilter,
  referenceInterface,
} from '@ardoq/reference-interface';
import {
  CellTypes,
  HeaderModel,
  HeaderType,
  LinkedComponents,
  TableViewModel,
  TableViewRow,
} from './common/types';
import {
  action$,
  ofType,
  reduceState,
  streamReducer,
  withNamespace,
} from '@ardoq/rxbeach';
import { Observable, combineLatest } from 'rxjs';
import { debounceTime, startWith } from 'rxjs/operators';
import * as profiling from '@ardoq/profiling';
import { logWarn } from '@ardoq/logging';
import { componentInterface } from '@ardoq/component-interface';
import { graphModel$ } from 'modelInterface/graphModel$';
import { context$ } from 'streams/context/context$';
import { startAction } from 'actions/utils';
import { currentUserPermissionContext$ } from 'streams/currentUserPermissions/currentUserPermissionContext$';
import {
  Locale,
  localeCompare,
  localeCompareNumericLowercase,
} from '@ardoq/locale';
import { components$ } from 'streams/components/components$';
import { locale$ } from 'streams/locale$';
import { i18nLocale$ } from 'streams/i18nLocale$';
import { t, type I18nLocale } from '@ardoq/i18n';
import { dateRangeOperations, getStartDateName } from '@ardoq/date-range';
import { ExtractStreamShape } from 'tabview/types';
import { mapValues } from 'lodash';

const ARDOQ_COMPONENT_PATH_PROPERTY_KEY = 'ardoq-component-path';

const getConfidentialFieldName = (fieldName: string): string => {
  if (dateRangeOperations.isCombinedDateRangeFieldNames(fieldName))
    return getStartDateName(fieldName);
  return fieldName;
};

export const getDefaultHeaders = (i18nLocale: I18nLocale): HeaderModel[] => [
  {
    key: 'component-key',
    label: t(i18nLocale, 'tableViews:component.ardoqId', 'Ardoq ID'),
    cellType: CellTypes.TEXT,
    isExpandController: false,
  },
  {
    key: 'id',
    label: t(i18nLocale, 'tableViews:component.ardoqOid', 'Ardoq OID'),
    cellType: CellTypes.TEXT,
    isExpandController: false,
  },
  {
    key: 'name',
    label: t(i18nLocale, 'tableViews:component.name', 'Name'),
    cellType: CellTypes.NAME,
    isExpandController: true,
  },
  {
    key: ARDOQ_COMPONENT_PATH_PROPERTY_KEY,
    label: t(i18nLocale, 'tableViews:component.path', 'Path'),
    cellType: CellTypes.PATH,
    isExpandController: false,
  },
  {
    key: 'parent',
    label: t(i18nLocale, 'tableViews:component.parent', 'Parent'),
    cellType: CellTypes.PARENT,
    isExpandController: false,
  },
  {
    key: 'type',
    label: t(
      i18nLocale,
      'tableViews:component.componentType',
      'Component type'
    ),
    cellType: CellTypes.TEXT,
    isExpandController: false,
  },
  {
    key: 'created',
    label: t(i18nLocale, 'tableViews:component.created', 'Created'),
    cellType: CellTypes.DATE_TIME,
    isExpandController: false,
  },
  {
    key: 'last-updated',
    label: t(i18nLocale, 'tableViews:component.lastUpdated', 'Last updated'),
    cellType: CellTypes.DATE_TIME,
    isExpandController: false,
  },
];

const hasData = () => Boolean(contextInterface.getActiveWorkspaceId());

const getAncestry = (
  component: APIComponentAttributes,
  componentsById: Record<ArdoqId, APIComponentAttributes>
): APIComponentAttributes[] => {
  const ancestry: APIComponentAttributes[] = [component];
  let parentId = component.parent;
  while (parentId) {
    const parent = componentsById[parentId];
    if (!parent) break;
    ancestry.unshift(parent);
    parentId = parent.parent;
  }
  return ancestry;
};

const componentPath = (
  component: APIComponentAttributes,
  componentsById: Record<ArdoqId, APIComponentAttributes>
) => [
  {
    type: 'workspace',
    name: workspaceInterface.getWorkspaceName(component.rootWorkspace),
  },
  ...getAncestry(component, componentsById).map(componentPropsFromData),
];

type GroupMap = Map<ArdoqId, Record<string, LinkedComponents>>;

type ComponentGroups = {
  keys: KeyMap;
  groups: GroupMap;
};

const toRow =
  ({
    fields,
    compGroups,
    permissionContext,
    componentsById,
    customFieldContext,
  }: {
    fields: APIFieldAttributes[];
    compGroups: GroupMap;
    permissionContext: PermissionContext;
    componentsById: Record<ArdoqId, APIComponentAttributes>;
    customFieldContext: TableCustomFieldContext;
  }) =>
  (component: APIComponentAttributes): TableViewRow => {
    const parentComponent = component.parent
      ? componentsById[component.parent]
      : undefined;
    const workspaceId = component.rootWorkspace;
    return {
      [ARDOQ_COMPONENT_PATH_PROPERTY_KEY]: componentPath(
        component,
        componentsById
      ),
      hasDescription: Boolean(component.description),
      markdownDescription: component.description || '',
      parent: parentComponent
        ? componentPropsFromData(parentComponent)
        : undefined,
      ...componentPropsFromData(component),
      ...(compGroups.get(component._id) || {}),
      ...mapValues(
        extractCustomFieldsFromComponentData(
          fields,
          component,
          customFieldContext
        ),
        (field, fieldName) => ({
          ...field,
          isConfidential: !permissionsOperations.canSeeConfidentialFieldByName(
            permissionContext,
            getConfidentialFieldName(fieldName),
            workspaceId
          ),
        })
      ),
      expandDescription: false,
    };
  };

const getFieldsForComponent = (
  component: APIComponentAttributes,
  allFields: APIFieldAttributes[]
) =>
  dateRangeOperations.mergeDateTimeFieldsToDateRangeFields(
    allFields.filter(
      field => field.model === component.model && field.componentType.length > 0
    )
  );

type KeyMap = Map<
  GlobalReferenceTypeId,
  {
    name: string;
    id: GlobalReferenceTypeId;
    outgoing: Set<ArdoqId>;
    incoming: Set<ArdoqId>;
  }
>;

const OUTGOING = 'outgoing';
const INCOMING = 'incoming';

type DIRECTION = typeof OUTGOING | typeof INCOMING;

const groupComponentsByReferenceTypes = (
  components: APIComponentAttributes[],
  graphModel: GraphModelShape,
  componentsById: Record<ArdoqId, APIComponentAttributes>,
  locale: Locale
) =>
  components.reduce<ComponentGroups>(
    (groupMap: ComponentGroups, component: APIComponentAttributes) => {
      const linkedComps = new Map<string, LinkedComponents>();
      const componentId = component._id;

      populateGroup(
        graphModel.sourceMap,
        componentId,
        groupMap,
        OUTGOING,
        linkedComps,
        componentsById
      );

      populateGroup(
        graphModel.targetMap,
        componentId,
        groupMap,
        INCOMING,
        linkedComps,
        componentsById
      );

      Array.from(linkedComps.values()).forEach(currentLinkedComps => {
        currentLinkedComps.value.sort((a, b) =>
          localeCompareNumericLowercase(a.name, b.name, locale)
        );
        currentLinkedComps.referencedComponentsAsString =
          currentLinkedComps.value.map(comp => comp.name).join();
      });

      groupMap.groups.set(componentId, Object.fromEntries(linkedComps));

      return groupMap;
    },
    {
      keys: new Map(),
      groups: new Map<string, Record<string, LinkedComponents>>(),
    }
  );

const populateGroup = (
  linkedMap: Map<ArdoqId, LinkedComponent[]>,
  componentId: string,
  groupMap: ComponentGroups,
  direction: DIRECTION,
  linkedComps: Map<string, LinkedComponents>,
  componentsById: Record<ArdoqId, APIComponentAttributes>
) =>
  (linkedMap.get(componentId) || []).forEach(
    ({ referenceId, componentId: linkedComponentId }) => {
      if (!isReferenceIdIncludedInContextByFilter(referenceId)) {
        return;
      }
      const referenceType =
        referenceInterface.getGlobalReferenceType(referenceId);
      const name = referenceType?.name ?? '';
      const id = referenceType?.id ?? '';
      let group = groupMap.keys.get(id);
      if (!group) {
        group = {
          name,
          id,
          [OUTGOING]: new Set(),
          [INCOMING]: new Set(),
        };
        groupMap.keys.set(id, group);
      }
      group[direction].add(linkedComponentId);

      accumulateLinkedComponent(
        name,
        direction,
        linkedComps,
        linkedComponentId,
        componentsById
      );
    }
  );

const accumulateLinkedComponent = (
  name: string,
  direction: DIRECTION,
  acc: Map<string, LinkedComponents>,
  linkedComponentId: ArdoqId,
  componentsById: Record<ArdoqId, APIComponentAttributes>
) => {
  const key = getKey(name, direction);

  const componentData = componentsById[linkedComponentId];
  if (!componentData) {
    logWarn(Error('Component not found in table view reducers'));
    // We should never enter here, but we do, basically because we are
    // mixing streams and backbone event driven updates.
    // The application will recover because the according relevant updates will
    // be triggered on the correct channel, but we do obviously too much
    // usless data allocations and we run into inconsitent
    // model states.
    //
    // Currently this happens with big datasets in presentations.
    //
    // Issue is tracked in ARD-3919.
    return;
  }
  const linkedComponent = componentPropsFromData(componentData);

  let linkedComponents = acc.get(key);
  if (!linkedComponents) {
    linkedComponents = {
      isDisabled: false,
      value: [],
      referencedComponentsAsString: '',
    };
    acc.set(key, linkedComponents);
  }
  linkedComponents.value.push(linkedComponent);
};

const getKey = (name: string, direction: DIRECTION) =>
  `${name}(${direction === OUTGOING ? 'out' : 'in'})`;

const getReferenceTypesFromGroups = (groupMap: KeyMap, locale: Locale) =>
  Array.from(groupMap.values())
    .flatMap(({ id, name, outgoing, incoming }) => [
      incoming.size > 0 && {
        label: name,
        key: getKey(name, INCOMING),
        id,
        type: HeaderType.SOURCE,
        cellType: CellTypes.REFERENCE_LIST,
        order: null,
      },
      outgoing.size > 0 && {
        label: name,
        key: getKey(name, OUTGOING),
        id,
        type: HeaderType.TARGET,
        cellType: CellTypes.REFERENCE_LIST,
        order: null,
      },
    ])
    .filter(isTruthy)
    .sort((a, b) => localeCompare(a.key, b.key, locale));

const sortRowsByReferences = (
  rows: (TableViewRow &
    Record<string, { referencedComponentsAsString: string }>)[],
  sort: ContextSort,
  locale: Locale
) => {
  return rows.sort((a, b) => {
    const aValue = a[sort.attr]
      ? a[sort.attr].referencedComponentsAsString
      : '';
    const bValue = b[sort.attr]
      ? b[sort.attr].referencedComponentsAsString
      : '';
    if (!aValue.length && !bValue.length) {
      return localeCompareNumericLowercase(a.name, b.name, locale);
    } else if (!aValue.length) {
      return 1;
    } else if (!bValue.length) {
      return -1;
    }
    return (
      localeCompareNumericLowercase(aValue, bValue, locale) *
      (sort.order === NumericSortOrder.ON_REFERENCE_DESCENDING ? -1 : 1)
    );
  });
};

type NextStateArgs = {
  components: APIComponentAttributes[];
  componentsById: Record<ArdoqId, APIComponentAttributes>;
  sort: ContextSort;
  graphModel: GraphModelShape;
  hasComponentsAvailable: boolean;
  contextComponentId?: ArdoqId;
  permissionContext: PermissionContext;
  fields: APIFieldAttributes[];
  locale: Locale;
  i18nLocale: I18nLocale;
  customFieldContext: TableCustomFieldContext;
};
const nextState = ({
  components,
  componentsById,
  sort,
  graphModel,
  hasComponentsAvailable,
  contextComponentId,
  permissionContext,
  fields,
  locale,
  i18nLocale,
  customFieldContext,
}: NextStateArgs): TableViewModel => {
  if (components.length === 0) {
    return {
      ...emptyState,
      rows: hasData() ? [] : null,
      hasComponentsAvailable,
      contextComponentId,
    };
  }
  const transaction = profiling.startTransaction(
    'table view model calculation',
    500,
    profiling.Team.INSIGHT
  );
  const filteredComponents = components.filter(component =>
    componentInterface.isIncludedInContextByFilter(component)
  );

  const compGroups = groupComponentsByReferenceTypes(
    filteredComponents,
    graphModel,
    componentsById,
    locale
  );

  const referenceTypes = getReferenceTypesFromGroups(compGroups.keys, locale);
  // Date range fields are represented as a comma separated string of their start
  // and end field names, so column-header sorting can compare on both.
  const customFields = getFieldsForComponent(components[0], fields).fields.map(
    field =>
      dateRangeOperations.isDateRangeField(field)
        ? {
            ...field,
            name: `${field.dateTimeFields.start.name},${field.dateTimeFields.end.name}`,
          }
        : field
  );
  const rows: TableViewRow[] = filteredComponents.map(
    toRow({
      fields: customFields,
      compGroups: compGroups.groups,
      permissionContext,
      componentsById,
      customFieldContext,
    })
  );
  const filteredReferenceTypes = referenceTypes
    .filter(isUnique('key'))
    .map(refType => ({
      ...refType,
      isExpandController: false,
    }));

  const newState = {
    ...emptyState,
    hasComponentsAvailable,
    contextComponentId,
    defaultHeaders: setupHeaders(sort, getDefaultHeaders(i18nLocale)),
    referenceTypeHeaders: setupHeaders(sort, filteredReferenceTypes),
    fieldHeaders: setupHeaders(sort, customFields.map(fieldHeaderProps)),
    rows:
      (sort.order === NumericSortOrder.ON_REFERENCE_ASCENDING ||
        sort.order === NumericSortOrder.ON_REFERENCE_DESCENDING) &&
      filteredReferenceTypes.find(ref => ref.key === sort.attr)
        ? sortRowsByReferences(rows, sort, locale)
        : rows,
  };
  profiling.endTransaction(transaction, {
    viewId: ViewIds.TABLEVIEW,
    metadata: {
      numberOfHeaders:
        newState.defaultHeaders.length +
        newState.referenceTypeHeaders.length +
        newState.fieldHeaders.length,
      numberofRows: rows ? rows.length : 0,
    },
  });
  return newState;
};

const changes$ = combineLatest([
  graphModel$,
  context$,
  currentUserPermissionContext$,
  fields$,
  components$,
  locale$,
  i18nLocale$,
  orgUsers$,
  documentArchiveAttachments$,
  action$.pipe(ofType(notifyFiltersChanged), startWith(startAction())),
]).pipe(
  debounceTime(50) // debounce this to avoid repeated resets when batch editing components in grid editor
);
export const resetOnChanges = (
  _: TableViewModel,
  [
    graphModel,
    context,
    permissionContext,
    fields,
    components,
    locale,
    i18nLocale,
    orgUsers,
    attachments,
  ]: ExtractStreamShape<typeof changes$>
) => {
  const includedComponentIds = new Set(
    getContextComponentChildren(context, components)
      .filter(component =>
        componentInterface.isIncludedInContextByFilter(component)
      )
      .map(({ _id }) => _id)
  );
  const { hasComponentsAvailable, relevantComponents } =
    relevantComponentsFromContextShape({
      context,
      components,
      locale,
      includedComponentIds,
    });

  return nextState({
    components: relevantComponents,
    componentsById: components.byId,
    sort: context.sort,
    graphModel,
    hasComponentsAvailable,
    contextComponentId: context.componentId,
    permissionContext,
    fields: fields.models,
    locale,
    i18nLocale,
    customFieldContext: {
      usersById: orgUsers.byId,
      attachmentsById: attachments.byId,
      i18nLocale,
    },
  });
};

export const createViewModel$ = (
  viewInstanceId: string
): Observable<TableViewModel> => {
  return action$.pipe(
    withNamespace(viewInstanceId),
    reduceState<TableViewModel>('tableViewModel$', emptyState, [
      toggleDescriptionReducer,
      resetExpandedDescriptionsReducer(ViewIds.TABLEVIEW),
      streamReducer(changes$, resetOnChanges),
    ]),
    // Starting the app with the table view we get 4 updates here.
    // For now fixing this with debouncing.
    debounceTime(50)
  );
};
